import { useEffect } from 'react';
import { useLocation } from 'wouter';
import { useAuth, useClerk } from '@clerk/react';
import { useGetReviewerClaims, getGetReviewerClaimsQueryKey, type ReviewerClaimGrant } from '@edison/api-client-react';
import { usePortalStore } from '@/store/portal-store.js';
import { useAuthStore } from '@/store/auth-store.js';
import { Shield, FileText, LogOut, AlertCircle, RefreshCw } from 'lucide-react';

/**
 * Reviewer landing page (Group B: authenticated external reviewers) — lists the signed-in
 * reviewer's own active claim access grants (GET /api/reviewer/claims). Selecting a claim enters
 * the shared audit-portal pages in grant mode, scoped to that claim.
 *
 * No main-app navigation is rendered here — reviewers never see the staff shell.
 */
export function ReviewerLandingPage() {
  const { isLoaded, isSignedIn } = useAuth();
  const { signOut } = useClerk();
  const [, navigate] = useLocation();
  const setGrantSession = usePortalStore((s) => s.setGrantSession);
  // App.tsx's useAuthFlow() runs unconditionally on every route (including this one) and syncs
  // the Clerk identity to our DB user record via GET /api/auth/me — for a reviewer's very first
  // sign-in, that sync is what reclaims their pending invitation row. Gate the claims query on
  // that sync having landed (role === 'external_reviewer'), not just on Clerk's isSignedIn: firing
  // the request the instant Clerk reports signed-in, before the sync completes, hits
  // requireReviewer before the DB row exists/is active and transiently 403s (Retry recovers, but
  // there's no reason to show that at all).
  const authUser = useAuthStore((s) => s.user);
  const reviewerSynced = authUser?.role === 'external_reviewer';

  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      navigate('/reviewer-login');
    }
  }, [isLoaded, isSignedIn, navigate]);

  const { data, isLoading: isFetchingClaims, error, refetch } = useGetReviewerClaims({
    query: { enabled: !!isLoaded && !!isSignedIn && reviewerSynced, queryKey: getGetReviewerClaimsQueryKey() },
  });
  // Show the same skeleton while waiting on the auth-store sync as while the (now-gated) query is
  // actually in flight — otherwise the disabled query's isLoading is false and the page would
  // flash "No claims shared with you yet" before the sync (and the real request) ever happens.
  const isLoading = isFetchingClaims || (!!isLoaded && !!isSignedIn && !reviewerSynced);

  const handleSelect = (claimId: string) => {
    setGrantSession(claimId);
    navigate('/reviewer/portal/ask');
  };

  const handleSignOut = () => {
    void signOut(() => navigate('/reviewer-login'));
  };

  if (!isLoaded || !isSignedIn) {
    // Either still loading the Clerk session, or the redirect effect above is about to fire.
    return (
      <div className="dark min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-gray-500 text-sm">Loading…</div>
      </div>
    );
  }

  return (
    <div className="dark min-h-screen bg-gray-950 text-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-lg space-y-6">
        <div className="text-center space-y-2">
          <Shield className="h-10 w-10 text-blue-400 mx-auto" aria-hidden="true" />
          <h1 className="text-xl font-semibold text-white">Your R&D claims</h1>
          <p className="text-sm text-gray-400">
            Select a claim below to view its evidence package.
          </p>
        </div>

        {isLoading && (
          <div className="space-y-3">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="h-16 w-full rounded-lg bg-gray-900 border border-gray-800 animate-pulse" />
            ))}
          </div>
        )}

        {!isLoading && !!error && (
          <div className="rounded-lg border border-red-900/50 bg-red-950/50 p-4 space-y-3 text-red-400 text-sm">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span>Failed to load your claims. Please try again.</span>
            </div>
            <button
              onClick={() => void refetch()}
              className="inline-flex items-center gap-2 rounded-lg border border-red-800 px-3 py-1.5 text-xs font-medium text-red-300 hover:bg-red-900/40"
            >
              <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
              Retry
            </button>
          </div>
        )}

        {!isLoading && !error && (data?.data?.items?.length ?? 0) === 0 && (
          <div className="text-center py-12 rounded-lg border border-gray-800 border-dashed">
            <FileText className="h-8 w-8 text-gray-600 mx-auto mb-3" aria-hidden="true" />
            <p className="text-sm text-gray-400">No claims shared with you yet.</p>
            <p className="text-xs text-gray-600 mt-1">
              Contact the claim owner if you're expecting access.
            </p>
          </div>
        )}

        {!isLoading && !error && (data?.data?.items?.length ?? 0) > 0 && (
          <ul className="space-y-3">
            {(data?.data?.items as ReviewerClaimGrant[]).map((grant) => (
              <li key={grant.claimId}>
                <button
                  onClick={() => handleSelect(grant.claimId)}
                  className="w-full text-left rounded-lg border border-gray-800 bg-gray-900 p-4 hover:bg-gray-800 transition-colors"
                >
                  <p className="text-sm font-medium text-white">
                    {grant.claimName ?? 'Untitled claim'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {grant.incomeYear ?? '—'} · {grant.role} · expires{' '}
                    {new Date(grant.expiresAt).toLocaleDateString('en-AU', {
                      day: '2-digit', month: 'short', year: 'numeric',
                    })}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        )}

        <button
          onClick={handleSignOut}
          className="w-full flex items-center justify-center gap-2 rounded-lg border border-gray-800 px-3 py-2 text-sm text-gray-400 hover:bg-gray-900"
        >
          <LogOut className="h-4 w-4" aria-hidden="true" />
          Sign out
        </button>
      </div>
    </div>
  );
}
